import { SelectedOption } from '@/generated/graphql';
import { VariantOptions, SeatConfiguration } from './types';

export function determineVariants(selectedOptions: SelectedOption[]) {
  const optionsMap = new Map();
  const seatConfigurations: SeatConfiguration[] = [];
  const variables: VariantOptions = {
    color1: '',
    color2: '',
    yearRange: '',
    bodyTrim: '',
    seatType: '',
    seatStyle: '',
    vehicleDetail: '',
    mirror: '',
    cabSize: '',
    bedSize: '',
    modelDetail: '',
    optionsMap,
    seatTypeOption: '',
    seatSubtype: '',
    seatConfigurations,
  };
  selectedOptions?.forEach((option) => {
    optionsMap.set(option.name, option.value);
    switch (option.name) {
      case 'Year':
        variables.yearRange = option.value;
        break;
      case 'Body/Trim':
      case 'Body & Trim':
      case 'Body':
      case 'Trim':
      case 'Submodel':
      case 'Body Type':
        variables.bodyTrim = option.value;
        break;
      case 'Color':
        // Split color by '/' and trim any extra spaces
        const colors = option.value.split('/').map((s) => s.trim());
        variables.color1 = colors[0] || '';
        variables.color2 = colors[1] || '';
        break;
      case 'Seat Set':
      case 'Seat Seat':
        variables.seatType = option.value;
        break;
      case 'Seat Style':
        variables.seatStyle = option.value;
        break;
      case 'Vehicle Detail':
      case 'Fitment':
        variables.vehicleDetail = option.value;
        // variables.yearRange = detectYearRange(option.value) || '';
        break;
      case 'Mirror':
      case 'Mirror Type':
        variables.mirror = option.value;
        break;
      case 'Cab Size':
      case 'Cab':
        variables.cabSize = option.value;
        break;
      case 'Bed Size':
      case 'Bed':
        variables.bedSize = option.value;
        break;
      case 'Model Detail':
        variables.modelDetail = option.value;
        break;
      case 'Front Seats':
      case 'Second Row Seats':
      case 'Third Row Seats':
      case 'Seat Type':
        // COLLECT seat configurations instead of overwriting
        seatConfigurations.push({
          seatTypeOption: option.name,
          seatSubtype: option.value,
        });
        // Keep backward compatibility - set to the last one processed
        variables.seatTypeOption = option.name;
        variables.seatSubtype = option.value;
        break;
      default:
        break;
    }
  });

  return variables;
}

/**
 * Determine submodels based on selectedOptions order from Shopify
 * First option -> submodel1, second -> submodel2, third -> submodel3
 */
export function determineSubmodelsFromSelectedOptions(selectedOptions: SelectedOption[]) {
  // Filter out Year, Color, Seat Set, Title, and Seat Type options as they're handled separately
  const submodelOptions = selectedOptions.filter(
    (option) =>
      option.name !== 'Year' &&
      option.name !== 'Color' &&
      option.name !== 'Seat Set' &&
      option.name !== 'Seat Seat' &&
      option.name !== 'Title' &&
      option.name !== 'Seat Type' &&
      option.name !== 'Front Seats' &&
      option.name !== 'Second Row Seats' &&
      option.name !== 'Third Row Seats'
  );

  return {
    submodel1: submodelOptions[0]?.value || '',
    submodel2: submodelOptions[1]?.value || '',
    submodel3: submodelOptions[2]?.value || '',
    variantNames: {
      variant1Name: submodelOptions[0]?.name || '',
      variant2Name: submodelOptions[1]?.name || '',
      variant3Name: submodelOptions[2]?.name || '',
    },
  };
}

/**
 * Legacy function for backward compatibility
 * @deprecated Use determineSubmodelsFromSelectedOptions instead
 */
export function determineSubmodel1(
  type: string,
  bodyTrim: string,
  vehicleDetail: string,
  cabSize: string | null
) {
  if (type === 'Seat Covers') {
    if (cabSize) {
      return cabSize;
    } else {
      return bodyTrim || vehicleDetail;
    }
  } else {
    return bodyTrim || vehicleDetail || cabSize;
  }
}

/**
 * Legacy function for backward compatibility
 * @deprecated Use determineSubmodelsFromSelectedOptions instead
 */
export function determineSubmodel2(
  type: string,
  vehicleDetail: string,
  bedSize: string,
  cabSize: string | null,
  bodyTrim: string | null
) {
  if (type === 'Seat Covers') {
    if (cabSize && bodyTrim) {
      return bodyTrim;
    } else if (cabSize && vehicleDetail) {
      return vehicleDetail;
    }
  } else {
    return bedSize;
  }
}

export function determineVariantNames(
  selectedOptionKeys: string[],
  extractedCabSize: string,
  extractedModelDetail: string
) {
  let variant1Name, variant2Name, variant3Name;
  if (extractedCabSize) {
    variant1Name = 'Cab Size';
  }
  if (extractedModelDetail) {
    variant2Name = 'Model Detail';
  }
}

export function hasSeatTypeVariants(handle: string, variants: SelectedOption[]): boolean {
  // Check handle pattern
  const handleHasSeatType = /custom-(front|rear|third)-seat-covers/.test(handle);

  // Check if variants contain seat type options
  const variantHasSeatType = variants.some((option) =>
    ['Front Seats', 'Second Row Seats', 'Third Row Seats', 'Seat Type'].includes(option.name)
  );

  return handleHasSeatType || variantHasSeatType;
}

// NEW: Extract seat type from various sources
export function extractSeatType(
  handle: string,
  seatTypeOption: string,
  seatType: string
): 'front' | 'rear' | 'third' | null {
  // Priority: handle pattern -> variant option -> seat type

  // From handle
  const handleMatch = handle.match(/custom-(front|rear|third)-seat-covers/);
  if (handleMatch) {
    return handleMatch[1] as 'front' | 'rear' | 'third';
  }

  // From seat type option name
  if (seatTypeOption) {
    if (seatTypeOption.includes('Front')) return 'front';
    if (seatTypeOption.includes('Second') || seatTypeOption.includes('Rear')) return 'rear';
    if (seatTypeOption.includes('Third')) return 'third';
  }

  // From seat type value
  if (seatType) {
    const normalized = seatType.toLowerCase();
    if (normalized.includes('front')) return 'front';
    if (normalized.includes('rear') || normalized.includes('second')) return 'rear';
    if (normalized.includes('third')) return 'third';
  }

  return null;
}
